import { clone, getTypeObject } from 'helpers/global.helper/global.helper'
import type { ReactNode } from 'react'
import React, { ReactElement, useMemo } from 'react'
import { dynamicClassName } from 'styles/helper'
import {
  registerAppendPropsWithDatasets,
  registerComponentHeight,
  registerDefaultProps,
  registerUiConfigComponent,
} from 'ui/uiConfig/factory'
import styles from './Table.module.less'
import type { TableColumn, TableProps, TableRow } from './Table.types'

function appendPropsWithDataset(_datasetInstruction: DatasetInstruction, previousProps: TableProps): TableProps {
  return {
    ...previousProps,
    columns: [...(previousProps?.columns || [])],
    rows: [...(previousProps?.rows || [])],
  }
}

const defaultColumns = [{ Header: `Column`, accessor: `AccessorID`, is_dataset: false }]
const defaultRows = [{ unit: ``, data_type: ``, AccessorID: `Row` }]

const defaultProps: Omit<TableProps, `datasets` | `children` | `uid` | `ignoreZoom`> = {
  columns: defaultColumns,
  rows: defaultRows,
  has_background_color: false,
  no_padding: false,
  no_header: false,
  title: ``,
}

function getCalculatedRowsForFirstIndexInDataset(
  _rows: TableRow[],
  columns: TableColumn[],
  datasets: Dataset[]
): Record<string, unknown>[] {
  // Create lookup maps for O(1) access instead of O(n) find() calls
  const columnMap = new Map<string, TableColumn>()
  for (const column of columns) {
    columnMap.set(column.accessor, column)
  }

  const datasetMap = new Map<string, Dataset>()
  for (const dataset of datasets) {
    datasetMap.set(dataset.return_id, dataset)
  }

  return _rows
    .map((originalRow) => {
      const row: TableRow = clone(originalRow)

      for (const key in row) {
        const column = columnMap.get(key)
        const returnId = row[key]

        if (!column || !returnId) {
          continue
        }

        if (typeof returnId !== 'string') {
          continue
        }

        const datasetItem = datasetMap.get(returnId)

        if (datasetItem && column.is_dataset) {
          const value = datasetItem.values[0] ?? 0
          const typeObj = getTypeObject(value, row.data_type, row.unit, row.decimals)
          row[key] = typeObj.value + ` ` + typeObj.unit
        } else if (row[key] === datasetItem?.return_id) {
          row[key] = `-`
        } else if (column.is_dataset) {
          row[key] = `-`
        }
      }

      return row
    })
    .reverse()
}

registerUiConfigComponent('table', Table)
registerAppendPropsWithDatasets('table', appendPropsWithDataset)
registerDefaultProps('table', defaultProps)
registerComponentHeight('table', 300)

export default function Table({
  columns,
  rows,
  title,
  datasets,
  has_background_color,
  no_padding,
  no_header,
}: TableProps): ReactElement {
  const tableColumns = useMemo(
    () =>
      columns.map((col) => ({
        Header: col.Header ?? '',
        accessor: col.accessor,
      })),
    [columns]
  )

  const tableData = useMemo(
    () => getCalculatedRowsForFirstIndexInDataset(rows, columns, datasets),
    [rows, columns, datasets]
  )

  if (!datasets.length && !tableData.length) {
    return <></>
  }

  return (
    <div className={styles.Table}>
      {title && <div className={styles.Table_Title}>{title}</div>}
      <div
        className={dynamicClassName({
          [styles.Table__backgroundColor]: has_background_color,
          [styles.Table_no_padding]: no_padding,
        })}
      >
        <table className={styles.SimpleTable}>
          {!no_header && (
            <thead
              className={dynamicClassName({
                [styles.SimpleTable_Head__backgroundColor]: has_background_color,
              })}
            >
              <tr>
                {tableColumns.map((col) => (
                  <th key={col.accessor} className={styles.SimpleTable_HeaderCell}>
                    {col.Header}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          <tbody className={styles.SimpleTable_Body}>
            {tableData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={dynamicClassName({
                  [styles.SimpleTable_Row__backgroundColor]: has_background_color,
                })}
              >
                {tableColumns.map((col) => (
                  <td key={col.accessor} className={styles.SimpleTable_Cell}>
                    {row[col.accessor] as ReactNode}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
